import React from "react";
import { Card, Flex, Text, FileUpload, Select } from "@jsq/ds/core";
import type { FileObjectType } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { FileWithType } from "../types";
import * as styles from "./styles_vanilla.css";

interface FileManagementSectionProps {
  filesWithTypes: FileWithType[];
  onFileSelected: (files: FileObjectType[]) => void;
  onDataTypeChange: (fileName: string, dataType: string | number | string[] | number[]) => void;
  onRemoveFile: (fileName: string) => void;
  getAvailableDataTypes: (
    currentFileDataType?: string,
  ) => Array<{ value: string; label: string }>;
  fileUploadKey: number;
}

export const FileManagementSection: React.FC<FileManagementSectionProps> = ({
  filesWithTypes,
  onFileSelected,
  onDataTypeChange,
  onRemoveFile,
  getAvailableDataTypes,
  fileUploadKey,
}) => {
  return (
    <Card className={styles.arenaSelectionCard}>
      <Flex flexDirection="column" gap={Sizes.MD}>
        <Flex flexDirection="column" gap={Sizes.SM}>
          <Flex alignItems="center" gap={Sizes.SM}>
            <Icon type="upload" size="medium" color={Colors.Primary500} />
            <Text variant="strong-lg" color={Colors.Neutral900}>
              Upload Files
            </Text>
          </Flex>
          <Text variant="regular-sm" color={Colors.Neutral600}>
            Upload OpCo CSV files
          </Text>
        </Flex>

        <div className={styles.fileUploadContainer}>
          <FileUpload
            key={fileUploadKey}
            canAllowMultiple
            showFileList={false}
            onFileSelected={onFileSelected}
            acceptedFileTypes={[".csv", ".tsv", "text/csv", "text/tab-separated-values"]}
            isFullWidth={true}
          />
        </div>

        {filesWithTypes.length > 0 && (
          <Flex flexDirection="column" gap={Sizes.MD}>
            <Flex flexDirection="column" gap={Sizes.SM}>
              <Text variant="strong-lg" color={Colors.Neutral900}>
                Configure Your Files
              </Text>
              <Text variant="regular-sm" color={Colors.Neutral600}>
                Select the file type for each file to continue
              </Text>
              <Text variant="regular-sm" color={Colors.Neutral600}>
                {filesWithTypes.length} file{filesWithTypes.length !== 1 ? "s" : ""}
              </Text>
            </Flex>

            <Flex flexDirection="column" gap={Sizes.SM}>
              {filesWithTypes.map((fileWithType) => (
                <Flex
                  key={fileWithType.file.file.name}
                  className={fileWithType.dataType ? styles.fileCardReady : styles.fileCard}
                  gap={Sizes.MD}
                  alignItems="flex-start"
                >
                  <Flex className={styles.fileIconContainer}>
                    <Icon
                      type="file-alt"
                      size="x-large"
                      color={fileWithType.dataType ? Colors.Success400 : Colors.Warning400}
                    />
                  </Flex>

                  <Flex
                    flexDirection="column"
                    gap={Sizes.SM}
                    className={styles.fileContentSection}
                  >
                    <Flex justifyContent="space-between" alignItems="flex-start">
                      <Flex flexDirection="column" gap={Sizes.XS}>
                        <Text variant="strong-xl" color={Colors.Neutral900}>
                          {fileWithType.file.file.name}
                        </Text>
                        <Text variant="regular-md" color={Colors.Neutral600}>
                          {(fileWithType.file.file.size / 1024).toFixed(1)} KB •{" "}
                          {fileWithType.file.file.name.toLowerCase().endsWith(".tsv")
                            ? "TSV"
                            : "CSV"}{" "}
                          File
                        </Text>
                      </Flex>
                      <Icon
                        type="times"
                        size="small"
                        color={Colors.Neutral500}
                        onClick={() => onRemoveFile(fileWithType.file.file.name)}
                        className={styles.clickableIcon}
                      />
                    </Flex>

                    {fileWithType.dataType ? (
                      <Flex alignItems="center" gap={Sizes.XS}>
                        <Text variant="regular-md" color={Colors.Neutral900}>
                          {
                            getAvailableDataTypes().find(
                              (dt) => dt.value === fileWithType.dataType,
                            )?.label
                          }
                        </Text>
                        <Flex alignItems="center" gap={Sizes.XS}>
                          <Icon type="check" size="small" color={Colors.Success400} />
                          <Text variant="regular-md" color={Colors.Success600}>
                            Ready
                          </Text>
                        </Flex>
                      </Flex>
                    ) : (
                      <Flex alignItems="center" gap={Sizes.XS}>
                        <Icon
                          type="exclamation-triangle"
                          size="medium"
                          color={Colors.Warning400}
                        />
                        <Text variant="regular-md" color={Colors.Warning600}>
                          Action Required
                        </Text>
                      </Flex>
                    )}

                    <Flex
                      className={styles.dataTypeSection}
                      flexDirection="column"
                      gap={Sizes.SM}
                    >
                      <Text variant="strong-sm" color={Colors.Neutral900}>
                        What type of data does this file contain?
                      </Text>
                      <Select
                        value={fileWithType.dataType || ""}
                        onChange={(value) =>
                          onDataTypeChange(fileWithType.file.file.name, value)
                        }
                        placeholder="Choose data type..."
                        full
                        className={styles.dataTypeSelect}
                      >
                        <Select.Section>
                          {getAvailableDataTypes(fileWithType.dataType).map((option) => (
                            <Select.Option key={option.value} value={option.value}>
                              {option.label}
                            </Select.Option>
                          ))}
                        </Select.Section>
                      </Select>
                    </Flex>
                  </Flex>
                </Flex>
              ))}
            </Flex>

            <Text variant="regular-sm" color={Colors.Neutral600}>
              Progress: {filesWithTypes.filter((f) => f.dataType).length} of{" "}
              {filesWithTypes.length} files configured
            </Text>
          </Flex>
        )}
      </Flex>
    </Card>
  );
};
